import React, { useState, useEffect, useCallback } from 'react';
import { Slider, Button, ButtonGroup, Popover, TextField } from '@mui/material';
import {Space, Typography, DatePicker} from 'antd';
import { ColorLens as ColorLensIcon, DeleteForeverRounded, DoneRounded } from '@mui/icons-material';
import { SwatchesPicker } from 'react-color';
import { Card } from '@mui/material';
import dayjs from "dayjs";
import DateRange from "../../Components/DateRange";
import { debounce } from 'lodash';
import Draggable from 'react-draggable';



const AnnotationConfig = ({
    annotation,
    onChange,
    onRemove,
    onClose,
}) => {

    console.log(annotation)
    const defaultColor = '#888cb8';
    const [color, setColor] = useState(defaultColor);
    const [thickness, setThickness] = useState(1); // Default thickness
    const [opacity, setOpacity] = useState(100);  // Default opacity (100%)
    const [anchorEl, setAnchorEl] = useState(null); // For color picker popover
    const [xAnchor, setXAnchor] = useState(null);
    const [xRange, setXRange] = useState([null, null]);
    const [labelText, setLabelText] = useState(""); // For label text
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const handleDrag = (e, data) => {
        setPosition({ x: data.x, y: data.y });
    };



    useEffect(() => {
        if (annotation) {
            const initialColor = annotation?.stroke?.().color || defaultColor;
            const initialThickness = annotation?.stroke?.().thickness || 1;
            const initialOpacity = annotation?.fill?.()?.opacity || 0.1;
            setColor(initialColor);
            setThickness(initialThickness);
            setOpacity(initialOpacity * 100); // Convert to percentage

            const x1 = annotation?.xAnchor?.() ? dayjs(annotation?.xAnchor?.()) : null;
            const x2 = annotation?.secondXAnchor?.() ? dayjs(annotation?.secondXAnchor?.()) : null;
            if (x1 && x2) {
                setXRange([x1, x2]);
            } else if (x1) {
                setXAnchor(x1);
            }
            if (annotation.getType() === 'label') {
                setLabelText(annotation.text());
            }
        }
    }, [annotation]);

    useEffect(() => {
        let deletePressed = false;
        let deletePressTime = 0;

        const handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                onClose();
            } else if (event.key === 'Backspace' || event.key === 'Delete') {
                if (!deletePressed) {
                    deletePressed = true;
                    deletePressTime = Date.now();
                } else if (Date.now() - deletePressTime > 1000) {
                    // Double click or press and hold for 1+ seconds detected
                    onRemove();
                    deletePressed = false;
                }
            } else {
                deletePressed = false;
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, onRemove]);


   const debouncedOnChange = useCallback(debounce((updatedProps) => {
        onChange(updatedProps);
    }, 300), [onChange]);

    useEffect(()=>{
        debouncedOnChange({color, thickness, opacity: opacity/100, xRange, labelText});
    }, [color, thickness, opacity, xRange, labelText, debouncedOnChange]);

    const handleColorChange = (color) => {
        setColor(color.hex);
        setAnchorEl(null); // Close the color picker popover
    };

    const handleThicknessChange = (event, value) => {
        setThickness(value);
    };

    const handleOpacityChange = (event, value) => {
        setOpacity(value);
    };

    const handleColorButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseColorPicker = () => {
        setAnchorEl(null);
    };

    const handleXAnchorChange = (date) => {
        setXRange([dayjs(date).valueOf(), null])
    };

    const handleXRangeChange = (startDate, endDate) => {
        console.log(startDate, endDate)
        const range = [dayjs(startDate).valueOf(), dayjs(endDate).valueOf()];
        setXRange(range);
    };

    const handleLabelTextChange = (event) => {
        setLabelText(event.target.value);
    };


    const open = Boolean(anchorEl);
    const minDate = dayjs('2015-01-01');
    const maxDate = dayjs();

    return (
        <Draggable position={position} onDrag={handleDrag}>

            <Card
                style={{
                    backgroundColor: '#FFF',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999, // Ensure it's above everything else
                    position: 'fixed', // Fix position to the top
                    top: 10, // Stick to the top
                    left: '35%'
                }}
            >
                <Space size="middle" align="center">
                    <Button color="error" variant="text" onClick={onRemove}>
                        <DeleteForeverRounded />
                    </Button>

                   {annotation.getType() === 'label' && (
                        <TextField
                            label="Label Text"
                            value={labelText}
                            onChange={handleLabelTextChange}
                            variant="outlined"
                            size="small"
                            style={{ width: 200 }}
                        />
                    )}

                    <Space direction="vertical" size="small" style={{ width: 100 }}>
                        <Typography.Text>Thickness</Typography.Text>
                        <Slider
                            value={thickness}
                            min={1}
                            max={10}
                            step={0.5}
                            onChange={handleThicknessChange}
                            aria-labelledby="thickness-slider"
                            valueLabelDisplay="auto"
                            color={'info'}
                        />
                    </Space>

                    {annotation.fill?.()?.hasOwnProperty('opacity') && (
                        <Space direction="vertical" size="small" style={{ width: 100 }}>
                            <Typography.Text>Opacity</Typography.Text>
                            <Slider
                                value={opacity}
                                min={0}
                                max={100}
                                step={1}
                                onChange={handleOpacityChange}
                                aria-labelledby="opacity-slider"
                                valueLabelDisplay="auto"
                                color={'info'}
                            />
                        </Space>
                    )}

                    <Space direction="vertical" size="small">
                        {xRange[0] || xRange[1] ? (
                            <DateRange
                                startDate={xRange[0]}
                                endDate={xRange[1]}
                                minDate={minDate}
                                maxDate={maxDate}
                                onDateRangeChange={handleXRangeChange}
                            />
                        ) : (
                            <DatePicker value={xAnchor} onChange={handleXAnchorChange} />
                        )}
                    </Space>

                    <ButtonGroup variant="outlined" color="primary">
                        <Button onClick={handleColorButtonClick} style={{ backgroundColor: color }} variant="contained">
                            <ColorLensIcon style={{ color: "#fff" }} />
                        </Button>
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleCloseColorPicker}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <SwatchesPicker color={color || defaultColor} onChange={handleColorChange} />
                        </Popover>
                        <Button variant="contained" color="info" onClick={onClose}>
                            <DoneRounded />
                        </Button>
                    </ButtonGroup>
                </Space>
            </Card>
        </Draggable>
    );
};

export default AnnotationConfig;
